import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Space, Button, Popover } from 'antd';
import { useReactToPrint } from 'react-to-print';

import BaseModal from 'components/modules/BaseModal';
import DownloadCSVConfirm from 'components/modules/DownloadCSVConfirm';
import Spin from 'components/modules/Spin';
import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import { resultQuestionnaireSelector } from 'flex/view/Questionnaire/ResultQuestionnaire/selectors';

import * as TagElementGenerator from '../../TagElementGenerator';
import { AnswerResultModal } from '../AnswerResultModal';
import ModalContents from './ModalContents';
import { NonNanCandidate } from './QuestionResults';

const ResultQuestionnaireModal = ({
    onCancel,
    onClickFullScreen,
    isExportCsv,
}: {
    onCancel: () => void;
    onClickFullScreen: (uuid: string) => void;
    isExportCsv: boolean;
}) => {
    const { isResultOpen, uuid: questionnaireUuid } = useAppSelector(state => state.questionnaireTab.modal);
    const { isOpen, item } = useAppSelector(state => state.resultQuestionnaire.modal);
    const { questionnaire } = useAppSelector(state => state.resultQuestionnaire);
    const loading = useAppSelector(resultQuestionnaireSelector.loading);
    const disabledCsvExport = useAppSelector(resultQuestionnaireSelector.disabledCsvExport);
    const loadingButtons = useAppSelector(resultQuestionnaireSelector.loadingButtons);
    const dispatch = useDispatch();
    const printTargetRef = useRef(null);
    const isFirstRender = useRef(false);
    const handlePrint = useReactToPrint({
        content: () => printTargetRef.current,
        documentTitle: `${questionnaire?.name}の結果`,
    });
    const [isAnimationLoading, setIsAnimationLoading] = useState(false);
    useEffect(() => {
        if (!isResultOpen) {
            setIsAnimationLoading(true);
            isFirstRender.current = false;
            return;
        }

        if (isResultOpen) {
            isFirstRender.current = true;
        }

        dispatch(Actions.view.viewLogic.resultQuestionnaire.prepareView());
    }, [dispatch, isResultOpen]);

    const checkTypeAnswerGraph = useCallback((item: Question) => {
        const allAnswersCount = item
            .answerCandidates
            .filter(byAnswerCountOverZero)
            .reduce((sum, candidate) => sum + candidate.answerCount, 0);

        if ((item.type === 1 || item.type === 2) && allAnswersCount !== 0) return true;
        return false;
    }, []);

    const byAnswerCountOverZero = (e: { answerCount: null | undefined | number }): e is NonNanCandidate => {
        if (e.answerCount === null) return false;
        if (e.answerCount === undefined) return false;
        if (e.answerCount === 0) return false;
        return true;
    };

    useEffect(() => {
        if (!questionnaire) return;
        let checkType = false;
        if (questionnaire.isListView) {
            checkType = questionnaire?.items.some(item => checkTypeAnswerGraph(item));
        }
        isFirstRender.current = false;

        if (!isExportCsv && disabledCsvExport) {
            setIsAnimationLoading(false);
        } else {
            setIsAnimationLoading(checkType);
        }
    }, [checkTypeAnswerGraph, questionnaire]);

    const handleClickFullScreen = () => {
        onClickFullScreen(questionnaireUuid ?? questionnaire.uuid);
    };

    const handleResultCancel = () => {
        dispatch(Actions.view.viewLogic.resultQuestionnaire.closeDetail());
    };

    const handleCsvDownload = (encoding: Encoding, _: unknown, isWithBelongs: boolean | undefined) => {
        dispatch(Actions.view.viewLogic.resultQuestionnaire.downloadCsv(questionnaireUuid ?? questionnaire.uuid, encoding, isWithBelongs));
        isFirstRender.current = false;
        setTimeout(() => {
            setIsAnimationLoading(false);
        }, 1000);
    };

    const handleClickAnswerDetail = (item: Question) => {
        setIsAnimationLoading(false);
        dispatch(Actions.view.viewLogic.resultQuestionnaire.openDetail(item));
    };

    const handleAnimationEnd = (isEnded = true) => {
        setIsAnimationLoading(isEnded ? false : true);
    };

    const handleCancel = () => {
        if (!isAnimationLoading) {
            onCancel();
        }
    };

    const footer = [
        <DownloadCSVConfirm
            disabled={isFirstRender.current ? isExportCsv : disabledCsvExport}
            disabledMessage={
                <p>
                    CSVエクスポートはすでに実行済みです。<br />
                    CSVのダウンロード状況については、画面左下からご確認ください
                </p>
            }
            handleOk={handleCsvDownload}
            key='download'
            loading={loadingButtons}
            size='large'
            isShowWithBelongs={true}
            isAnonymous={questionnaire?.isAnonymous}
        />,
        <Popover
            content={
                '印刷設定画面が表示されましたら、' +
                '送信先を「PDFに保存」を選択して、' +
                '「保存」ボタンをクリックしてください'
            }
            key='popover'
        >
            <Button
                disabled={loadingButtons || isAnimationLoading || loading}
                loading={loadingButtons || isAnimationLoading || loading}
                onClick={handlePrint}
                size='large'
            >
                PDFエクスポート
            </Button>
        </Popover>,
        <Button
            key='full-screen'
            loading={loadingButtons || isAnimationLoading}
            onClick={handleClickFullScreen}
            size='large'
        >
            全画面表示
        </Button>,
        <Button
            key='back'
            disabled={loadingButtons || isAnimationLoading}
            onClick={handleCancel} size='large'
        >
            閉じる
        </Button>,
    ];

    return (
        <BaseModal
            className='common-modal questionnaire-result questionnaire-preview'
            footer={footer}
            onCancel={handleCancel}
            style={{ top: 20, width: '97vw !important' }}
            title={questionnaire ? <ModalTitle questionnaire={questionnaire} /> : ''}
            visible={isResultOpen}
            maskClosable={false}
        >
            <Spin
                spinning={loading}
                style={{ minHeight: '100px' }}
                tip='しばらくお待ち下さい...'
            >
                {questionnaire ?
                    <ModalContents
                        onClickAnswerDetail={handleClickAnswerDetail}
                        printTargetRef={printTargetRef}
                        questionnaire={questionnaire}
                        onAnimationEnd={handleAnimationEnd}
                    /> :
                    <div style={{ minHeight: '100px' }} />
                }
            </Spin>

            {/* AnswerResultModal は BaseModal 内にないと後ろに隠れてしまう*/}
            <AnswerResultModal
                isOpen={isOpen}
                item={item}
                onCancel={handleResultCancel}
                questionnaire={questionnaire}
            />
        </BaseModal>
    );
};


/**
 * アンケートのモーダルにつけるたいとるについて変更する
 */
const ModalTitle = ({ questionnaire }: { questionnaire: Questionnaire }) => {
    const tagNode = TagElementGenerator.generate(
        TagElementGenerator.TYPES_QUESTIONNAIRE_PUBLIC,
        questionnaire.isPublic,
    );
    const tagNode2 = TagElementGenerator.generate(
        TagElementGenerator.TYPES_QUESTIONNAIRE_ANONYMOUS,
        questionnaire.isAnonymous,
    );
    const tagNode3 = TagElementGenerator.generate(
        TagElementGenerator.TYPES_QUESTIONNAIRE_PUBLIC_RESULT,
        questionnaire.isPublicResult,
    );

    return (
        <Space size={8}>
            {tagNode}{tagNode2}{tagNode3}<span>{questionnaire.name}</span>
        </Space>
    );
};

export default ResultQuestionnaireModal;
